<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, watch, onBeforeUnmount } from "vue"
import { useI18n } from "vue-i18n"
import { useTaskStore } from "@core/stores/task.store"

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * Valeur actuelle de l'ID source
     */
    value?: string
    /**
     * Indique si on est en mode édition
     */
    isEditing?: boolean
}

interface SearchResult {
    idsource: string
    idtype: string
    operator: string
    apikey: string
    models: {
        sourcebrand: string
        model: string
    }[]
}

interface Emits {
    /**
     * Émis lorsque l'ID source change
     */
    (e: "input", value: string): void
    /**
     * Émis lorsque la recherche retourne un résultat
     */
    (e: "result", value: SearchResult): void
    /**
     * Émis pour basculer vers la saisie manuelle
     */
    (e: "click:manually"): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    value: "",
    isEditing: false,
})

const emit = defineEmits<Emits>()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            form: {
                title: "Recherche d'un capteur",
                placeholder: "Identifiant du capteur",
                button_search: "Rechercher",
                button_manually: "Saisie manuelle",
                task_label: "Recherche du capteur",
                task_error: "Aucun capteur trouvé avec cet identifiant.",
                popup: "Saisissez l'identifiant du capteur pour rechercher ses informations.",
            },
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const taskStore = useTaskStore()

// ================================
// ÉTAT DES DONNÉES
// ================================
/**
 * Valeur interne de l'ID source
 */
const modelValue = ref<string>(props.value || "")

/**
 * Indicateur de recherche en cours
 */
const isSearching = ref<boolean>(false)

/**
 * Identifiant de la tâche en cours
 */
const taskId = ref<string | null>(null)

/**
 * Résultat de la recherche
 */
const result = ref<SearchResult | null>(null)

/**
 * Message d'erreur
 */
const errorMessage = ref<string>("")

// ================================
// DONNÉES CALCULÉES
// ================================
/**
 * Vérifie si le bouton de recherche peut être activé
 */
const getCanSearch = computed((): boolean => {
    return !!modelValue.value && !isSearching.value
})

// ================================
// WATCHERS
// ================================
/**
 * Synchronise la valeur externe avec la valeur interne
 */
watch(
    () => props.value,
    (newValue) => {
        modelValue.value = newValue || ""
    }
)

/**
 * Émet l'événement input lorsque la valeur change
 */
watch(modelValue, (newValue) => {
    emit("input", newValue)
})

// ================================
// LOGIQUE MÉTIER
// ================================
/**
 * Lance la recherche du capteur
 */
async function onSearch(): Promise<void> {
    if (!modelValue.value || isSearching.value) return

    errorMessage.value = ""
    isSearching.value = true
    result.value = null

    try {
        const { task_id } = await taskStore.launchTask(
            "source.find_config.request",
            { idsource: modelValue.value },
            undefined,
            {
                taskname: t("form.task_label"),
                notification: false,
                autoDelete: true,
                onReceived: () => {
                    // Tâche reçue
                },
                onSuccess: (response: any) => {
                    // Tâche réussie - récupère le résultat
                    let source = null

                    if (response?.data) {
                        source =
                            response.data?.result?.source ||
                            response.data?.result ||
                            response.data?.source ||
                            response.data
                    } else if (taskId.value) {
                        const task = taskStore.getTaskById(taskId.value)
                        const taskReturn = task?.return
                        source =
                            taskReturn?.data?.result?.source ||
                            taskReturn?.data?.result ||
                            taskReturn?.result?.source ||
                            taskReturn?.result ||
                            taskReturn?.data
                    }

                    if (
                        source &&
                        typeof source === "object" &&
                        Object.keys(source).length > 0
                    ) {
                        result.value = source
                        emit("result", source)
                    } else {
                        errorMessage.value = t("form.task_error")
                    }

                    isSearching.value = false
                    result.value = null
                },
                onError: () => {
                    errorMessage.value = t("form.task_error")
                    isSearching.value = false
                    result.value = null
                },
            }
        )

        taskId.value = task_id
    } catch (error) {
        console.error("Erreur lors du lancement de la recherche:", error)
        errorMessage.value = t("form.task_error")
        isSearching.value = false
    }
}

/**
 * Gère le clic sur le bouton de saisie manuelle
 */
function onClickManually(): void {
    emit("click:manually")
}

// ================================
// LIFECYCLE
// ================================

/**
 * Nettoie la tâche si elle existe encore
 */
onBeforeUnmount(() => {
    if (taskId.value) {
        taskStore.removeTask(taskId.value)
    }
})
</script>

<template>
    <div class="flex flex-col gap-6">
        <!-- ================================ -->
        <!-- TITRE -->
        <!-- ================================ -->
        <h1 class="text-xl font-semibold text-gray-700 text-center">
            {{ t("form.title") }}
        </h1>

        <!-- ================================ -->
        <!-- RECHERCHE EN COURS -->
        <!-- ================================ -->
        <div
            v-if="isSearching"
            class="flex flex-col items-center justify-center gap-4 p-8"
        >
            <BaseProgressBar mode="indeterminate" />
            <p class="text-gray-600">{{ t("form.task_label") }}...</p>
        </div>

        <!-- ================================ -->
        <!-- FORMULAIRE DE RECHERCHE -->
        <!-- ================================ -->
        <div v-else class="flex flex-col gap-6">
            <!-- ================================ -->
            <!-- CHAMP DE RECHERCHE -->
            <!-- ================================ -->
            <BaseInputWrapper
                input-id="sensor-search-idsource"
                :label="t('form.placeholder')"
                :hint="t('form.popup')"
            >
                <BaseInputText
                    v-model="modelValue"
                    input-id="sensor-search-idsource"
                    :placeholder="t('form.placeholder')"
                    :disabled="isSearching"
                    @keyup.enter="onSearch"
                />
            </BaseInputWrapper>

            <!-- ================================ -->
            <!-- BOUTONS D'ACTION -->
            <!-- ================================ -->
            <div class="flex flex-col sm:flex-row gap-4">
                <BaseButton
                    :label="t('form.button_search')"
                    severity="primary"
                    :disabled="!getCanSearch"
                    :loading="isSearching"
                    class="flex-1"
                    @click="onSearch"
                />
                <BaseButton
                    :label="t('form.button_manually')"
                    severity="secondary"
                    class="flex-1"
                    @click="onClickManually"
                />
            </div>
        </div>

        <!-- ================================ -->
        <!-- MESSAGE D'ERREUR -->
        <!-- ================================ -->
        <BaseAppMessage
            v-if="errorMessage"
            severity="error"
            :message="errorMessage"
        />
    </div>
</template>
